import React, { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Plus, Trash2, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { createSubmission } from "@/lib/api";

const TIERS = ["Economy", "Standard", "Express", "Premium"];

const emptyCard = () => ({ year: "", set_name: "", player: "", card_number: "", sport: "Basketball", notes: "" });

const emptyShipping = {
  full_name: "",
  email: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "USA",
};

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.25em] text-white/50 font-semibold mb-2">{children}</label>
);

const Input = ({ testId, ...props }) => (
  <input
    data-testid={testId}
    {...props}
    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-colors"
  />
);

const Select = ({ testId, children, ...props }) => (
  <select
    data-testid={testId}
    {...props}
    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-colors"
  >
    {children}
  </select>
);

const Textarea = ({ testId, ...props }) => (
  <textarea
    data-testid={testId}
    {...props}
    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-colors min-h-[100px]"
  />
);

const StepDot = ({ n, current, label }) => (
  <div className="flex items-center gap-3">
    <div
      className={`w-9 h-9 rounded-full flex items-center justify-center font-display font-bold text-sm border transition-all ${
        current > n
          ? "bg-brand border-brand text-ink-900"
          : current === n
          ? "border-brand text-brand bg-brand/10"
          : "border-white/15 text-white/40"
      }`}
    >
      {current > n ? <Check size={16} /> : n}
    </div>
    <div className={`hidden md:block text-xs uppercase tracking-[0.2em] font-semibold ${current >= n ? "text-white" : "text-white/40"}`}>
      {label}
    </div>
  </div>
);

export const Submission = () => {
  const [step, setStep] = useState(1);
  const [tier, setTier] = useState("Standard");
  const [cards, setCards] = useState([emptyCard()]);
  const [shipping, setShipping] = useState(emptyShipping);
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);

  const totalSteps = 4;

  const canNext = useMemo(() => {
    if (step === 1) return !!tier;
    if (step === 2) return cards.every((c) => c.year && c.set_name && c.player && c.card_number);
    if (step === 3) {
      const s = shipping;
      return s.full_name && s.email && s.address_line1 && s.city && s.state && s.postal_code && s.country;
    }
    return true;
  }, [step, tier, cards, shipping]);

  const updateCard = (i, key, val) => setCards((prev) => prev.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));
  const addCard = () => setCards((p) => [...p, emptyCard()]);
  const removeCard = (i) => setCards((p) => p.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await createSubmission({ tier, cards, shipping });
      setReference(res.reference);
      setStep(4);
      toast.success("Submission received", { description: `Your reference: ${res.reference}` });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error("Submission failed", { description: typeof msg === "string" ? msg : "Please check your details." });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1);
    setTier("Standard");
    setCards([emptyCard()]);
    setShipping(emptyShipping);
    setReference(null);
  };

  return (
    <section id="submit" className="relative py-24 md:py-32" data-testid="submit-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-brand/15 blur-[160px]" />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="chip mb-6 mx-auto">Submit Your Cards</div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
            Start a <span className="logo-cursive text-brand">Blue</span> submission
          </h2>
          <p className="mt-6 text-white/60 max-w-2xl mx-auto">
            Four quick steps — pick a tier, enter your cards, tell us where to ship, and we'll lock in your slot.
          </p>
        </div>

        {/* Stepper */}
        {step < 4 && (
          <div className="glass rounded-2xl p-4 md:p-6 mb-6" data-testid="submission-stepper">
            <div className="flex items-center justify-between gap-2 md:gap-6">
              <StepDot n={1} current={step} label="Tier" />
              <div className="flex-1 h-px bg-white/10" />
              <StepDot n={2} current={step} label="Cards" />
              <div className="flex-1 h-px bg-white/10" />
              <StepDot n={3} current={step} label="Shipping" />
            </div>
          </div>
        )}

        <div className="glass rounded-2xl p-6 md:p-10">
          {/* STEP 1 - Tier */}
          {step === 1 && (
            <div data-testid="step-tier">
              <h3 className="font-display text-2xl font-bold mb-2">Choose a service tier</h3>
              <p className="text-white/60 mb-8 text-sm">Every tier uses the same AI grading pipeline — only speed and handling change.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TIERS.map((t) => (
                  <button
                    key={t}
                    data-testid={`tier-option-${t.toLowerCase()}`}
                    onClick={() => setTier(t)}
                    className={`text-left rounded-xl border p-5 transition-all ${
                      tier === t
                        ? "border-brand bg-brand/10"
                        : "border-white/10 bg-black/30 hover:border-brand/40"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display text-xl font-bold">{t}</span>
                      {tier === t && <Check size={18} className="text-brand" />}
                    </div>
                    <div className="mt-2 text-white/50 text-xs uppercase tracking-[0.2em]">
                      {t === "Economy" && "45 business days"}
                      {t === "Standard" && "20 business days"}
                      {t === "Express" && "7 business days"}
                      {t === "Premium" && "48 hours"}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2 - Cards */}
          {step === 2 && (
            <div data-testid="step-cards">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-2xl font-bold">Card details</h3>
                <button onClick={addCard} data-testid="add-card-btn" className="btn-secondary !py-2 !px-4 text-xs">
                  <Plus size={14} /> Add Card
                </button>
              </div>

              <div className="space-y-6">
                {cards.map((c, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-black/30 p-5" data-testid={`card-row-${i}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="font-display font-bold text-brand text-sm tracking-[0.2em]">CARD {String(i + 1).padStart(2, "0")}</div>
                      {cards.length > 1 && (
                        <button
                          onClick={() => removeCard(i)}
                          data-testid={`remove-card-${i}`}
                          className="text-white/40 hover:text-red-400 transition-colors"
                          aria-label="Remove card"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <FieldLabel>Year</FieldLabel>
                        <Input testId={`card-${i}-year`} placeholder="2023" value={c.year} onChange={(e) => updateCard(i, "year", e.target.value)} />
                      </div>
                      <div>
                        <FieldLabel>Set / Brand</FieldLabel>
                        <Input testId={`card-${i}-set`} placeholder="Panini Prizm" value={c.set_name} onChange={(e) => updateCard(i, "set_name", e.target.value)} />
                      </div>
                      <div>
                        <FieldLabel>Sport</FieldLabel>
                        <Select testId={`card-${i}-sport`} value={c.sport} onChange={(e) => updateCard(i, "sport", e.target.value)}>
                          <option>Basketball</option>
                          <option>Football</option>
                          <option>Baseball</option>
                          <option>Soccer</option>
                          <option>Hockey</option>
                          <option>Other</option>
                        </Select>
                      </div>
                      <div>
                        <FieldLabel>Player</FieldLabel>
                        <Input testId={`card-${i}-player`} placeholder="Victor Wembanyama" value={c.player} onChange={(e) => updateCard(i, "player", e.target.value)} />
                      </div>
                      <div>
                        <FieldLabel>Card #</FieldLabel>
                        <Input testId={`card-${i}-number`} placeholder="136" value={c.card_number} onChange={(e) => updateCard(i, "card_number", e.target.value)} />
                      </div>
                      <div className="md:col-span-3">
                        <FieldLabel>Notes (optional)</FieldLabel>
                        <Textarea testId={`card-${i}-notes`} placeholder="Parallels, autographs, special requests…" value={c.notes} onChange={(e) => updateCard(i, "notes", e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3 - Shipping */}
          {step === 3 && (
            <div data-testid="step-shipping">
              <h3 className="font-display text-2xl font-bold mb-2">Return shipping</h3>
              <p className="text-white/60 mb-8 text-sm">Where should we send your freshly-slabbed cards?</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Full Name</FieldLabel>
                  <Input testId="ship-name" value={shipping.full_name} onChange={(e) => setShipping({ ...shipping, full_name: e.target.value })} placeholder="Jane Collector" />
                </div>
                <div>
                  <FieldLabel>Email</FieldLabel>
                  <Input testId="ship-email" type="email" value={shipping.email} onChange={(e) => setShipping({ ...shipping, email: e.target.value })} placeholder="you@example.com" />
                </div>
                <div>
                  <FieldLabel>Phone (optional)</FieldLabel>
                  <Input testId="ship-phone" value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} placeholder="+1 (555) 123-4567" />
                </div>
                <div>
                  <FieldLabel>Country</FieldLabel>
                  <Input testId="ship-country" value={shipping.country} onChange={(e) => setShipping({ ...shipping, country: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>Address Line 1</FieldLabel>
                  <Input testId="ship-line1" value={shipping.address_line1} onChange={(e) => setShipping({ ...shipping, address_line1: e.target.value })} placeholder="123 Main St" />
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>Address Line 2 (optional)</FieldLabel>
                  <Input testId="ship-line2" value={shipping.address_line2} onChange={(e) => setShipping({ ...shipping, address_line2: e.target.value })} placeholder="Apt / Suite" />
                </div>
                <div>
                  <FieldLabel>City</FieldLabel>
                  <Input testId="ship-city" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} />
                </div>
                <div>
                  <FieldLabel>State / Region</FieldLabel>
                  <Input testId="ship-state" value={shipping.state} onChange={(e) => setShipping({ ...shipping, state: e.target.value })} />
                </div>
                <div>
                  <FieldLabel>Postal Code</FieldLabel>
                  <Input testId="ship-postal" value={shipping.postal_code} onChange={(e) => setShipping({ ...shipping, postal_code: e.target.value })} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 4 - Success */}
          {step === 4 && reference && (
            <div className="text-center py-6" data-testid="step-success">
              <div className="mx-auto w-16 h-16 rounded-full bg-brand/15 flex items-center justify-center">
                <Check size={28} className="text-brand" />
              </div>
              <h3 className="mt-6 font-display text-3xl md:text-4xl font-bold">Submission received.</h3>
              <p className="mt-3 text-white/60">Your cards are queued for the AI grading pipeline.</p>

              <div className="mt-8 inline-flex items-center gap-3 px-5 py-4 rounded-xl border border-brand/40 bg-brand/10">
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-brand font-bold">Reference Number</div>
                  <div className="font-mono text-white text-xl font-bold mt-1" data-testid="submission-reference">{reference}</div>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(reference);
                    toast.success("Copied to clipboard");
                  }}
                  data-testid="copy-reference"
                  className="text-white/70 hover:text-brand transition-colors"
                  aria-label="Copy reference"
                >
                  <Copy size={18} />
                </button>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={reset} data-testid="new-submission-btn" className="btn-secondary">New Submission</button>
                <button
                  onClick={() => document.getElementById("top")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="back-to-top-btn"
                  className="btn-primary"
                >
                  Back to Top
                </button>
              </div>
            </div>
          )}

          {/* Nav */}
          {step < 4 && (
            <div className="mt-10 flex items-center justify-between gap-3">
              <button
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                data-testid="prev-step-btn"
                className="btn-secondary disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={14} /> Back
              </button>
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => Math.min(totalSteps, s + 1))}
                  disabled={!canNext}
                  data-testid="next-step-btn"
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!canNext || loading}
                  data-testid="submit-btn"
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loading ? "Submitting…" : "Submit to Blue"} <ArrowRight size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Submission;
